import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import MarkdownRenderer from 'react-markdown-renderer'
import { forceHideModal, showModal } from '../../../../actions/_modal'
import colors from '../../../../configuration/colors'
import withApiData from '../../../HOC/withApiData'
import LoadingSpinner from '../../../mini/LoadingSpinner'
import ModalFullScreen from '../../../mini/ModalFullScreen'
import { CourseImage } from '../../../styled/CourseImage'
import { MasteryLevelImage } from '../../../styled/MasteryLevelImage'
import FlexAlignCenter from '../../../styled/FlexAlignCenter'
import CloseModal from '../../../styled/CloseModal'
import { fetchLevelDetails, fetchLevelProgress } from './API'
import { Container, ModalTop } from './_Styled'
import LevelQuestsMentor from './LevelQuestsMentor'
import SetTargetDateNew from '../../../mini/TargetDates/SetTargetDateNew'
import MentorGuidance, {
  MENTOR_GUIDANCE_TYPE,
  MentorGuidanceContext
} from '../../../Mentor/Shared/MentorGuidance/Index'
import { H1, P1 } from '../../../styled/typography/typography'
import ActivityCourseCourseImage from '../../../styled/ActivityCourseCourseImage'
import useIsActivityCourse from '../../../../hooks/useIsActivityCourse'
import { getUnsavedWorkMessage, willLoseUnsavedWork } from '../../../../utils/unsavedwork'
import useLexicon from '../../../../hooks/useLexicon'

const ModalBody = styled(FlexAlignCenter)`
  padding: 31px 0;
  background: #f8f8f8;
  justify-content: center;
  flex-direction: column;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  max-height: 90%;
  text-align: left;

  .quest-btn.save {
    z-index: 0;
  }
`

const MarkdownWrapper = styled(P1)`
  color: ${colors.dove};
  width: 500px;

  p:first-child {
    margin-top: 0 !important;
  }

  p:last-child {
    margin-bottom: 0 !important;
  }
`

const LevelModalMentor = ({
  dispatch,
  apiData,
  level,
  quests,
  handleQuestClick,
  courseColor,
  ids,
  userId,
  courseSectionId,
  unsavedWork
}) => {
  const isActivityCourse = useIsActivityCourse(courseSectionId)
  const [isFetching, setIsFetching] = useState(true)
  const [isClosing, setIsClosing] = useState(false)
  const [levelDetails, setLevelDetails] = useState(null)
  const [levelProgress, setLevelProgress] = useState(null)
  const lexicon = useLexicon()

  let requiredQuests = []
  let choiceQuests = []

  quests.forEach(q => {
    if (q.required) {
      requiredQuests.push({
        ...q
      })
    } else {
      choiceQuests.push({
        ...q
      })
    }
  })

  const closeTheModal = () => {
    if (
      !willLoseUnsavedWork({ unsavedWork, isInModal: true }) ||
      window.confirm(getUnsavedWorkMessage(true))
    ) {
      setIsClosing(true)

      setTimeout(() => {
        dispatch(forceHideModal())
      }, 500)
    }
  }

  useEffect(() => {
    async function fetchData() {
      const levelDetails = await fetchLevelDetails(
        level.id,
        apiData,
        ids.masterCourseId,
        ids.courseId
      )

      const progress = await fetchLevelProgress(
        level.id,
        apiData,
        ids.courseSectionId,
        userId
      )

      setLevelDetails(levelDetails)
      setLevelProgress(progress)
      setIsFetching(false)
      setIsClosing(false)
    }

    fetchData()
  }, [level.id])

  return (
    <ModalFullScreen
      padding={'50px 0 0'}
      allowEscape
      closeCallback={closeTheModal}
      isClosing={isClosing}
      backgroundColor="rgba(36, 54, 70, 0.4)"
      allowCloseOnExternalClick
      bodyStyles={{ width: 'fit-content' }}
    >
      <CloseModal size={21} offWhiteIcon />
      <Container
        onClick={e => {
          e.stopPropagation()
        }}
        style={{
          overflow: 'visible' // allow full display of date picker
        }}
      >
        {isFetching ? (
          <div style={{ minHeight: '300px' }}>
            <LoadingSpinner />
          </div>
        ) : (
          <React.Fragment>
            <ModalTop
              color={levelDetails.master_course.color}
              style={{ borderRadius: '20px 20px 0 0' }}
            >
              {levelDetails.mastery ? (
                <MasteryLevelImage
                  style={{
                    height: '116px'
                  }}
                  courseColor={levelDetails.master_course.color}
                />
              ) : isActivityCourse ? (
                <ActivityCourseCourseImage
                  style={{
                    height: '116px'
                  }}
                  courseColor={levelDetails.master_course.color}
                />
              ) : (
                levelDetails.master_course.master_course_avatar && (
                  <CourseImage
                    style={{
                      height: '116px'
                    }}
                    imageUrl={
                      levelDetails.master_course.master_course_avatar.photo_url
                    }
                    courseColor={levelDetails.master_course.color}
                  />
                )
              )}
              <FlexAlignCenter
                style={{
                  position: 'relative',
                  width: '100%',
                  justifyContent: 'space-between'
                }}
              >
                <H1 white>
                  {level.mastery ? `Mastery ${lexicon.level}` : `${lexicon.level} ${level.number}`}
                </H1>
                <SetTargetDateNew
                  dispatch={dispatch}
                  levelId={level.id}
                  courseSectionId={ids.courseSectionId}
                  targetDate={
                    levelProgress ? levelProgress.mentor_target_on : null
                  }
                />
              </FlexAlignCenter>
            </ModalTop>

            <ModalBody>
              <MarkdownWrapper className="markdown">
                <MarkdownRenderer
                  markdown={levelDetails.description}
                  options={{
                    linkTarget: '_blank'
                  }}
                />
              </MarkdownWrapper>

              <div style={{ width: '500px', margin: '10px 0' }}>
                <MentorGuidanceContext.Provider
                  value={{
                    courseSectionId: ids.courseSectionId,
                    levelId: level.id,
                    questId: null,
                    activityId: null,
                    isEnrolled: true,
                    paddingLR: 0,
                    rightOffsetDeleteButton: 80,
                    background: '#f8f8f8',
                    sessionId: `level_modal/${ids.courseSectionId}/${level.id}`,
                    isInModal: true
                  }}
                >
                  <MentorGuidance
                    type={MENTOR_GUIDANCE_TYPE.LEVEL}
                    deleteConfirmationFollowupAction={() => {
                      dispatch(
                        showModal('LEVEL_MODAL_MENTOR', {
                          ids,
                          level,
                          quests,
                          handleQuestClick,
                          courseColor
                        })
                      )
                    }}
                  />
                </MentorGuidanceContext.Provider>
              </div>

              {requiredQuests.length > 0 && (
                <LevelQuestsMentor
                  quests={requiredQuests}
                  count={levelDetails.required_quests_count}
                  label={lexicon.core}
                  handleQuestClick={handleQuestClick}
                  courseColor={courseColor}
                  courseSectionId={courseSectionId}
                  isActivityCourse={isActivityCourse}
                  orgId={apiData.orgId}
                />
              )}
              {choiceQuests.length > 0 && (
                <LevelQuestsMentor
                  quests={choiceQuests}
                  count={
                    levelDetails.minimum_optional_quests_required_for_completion
                  }
                  label={lexicon.choice}
                  handleQuestClick={handleQuestClick}
                  courseColor={courseColor}
                  courseSectionId={courseSectionId}
                  isActivityCourse={isActivityCourse}
                  orgId={apiData.orgId}
                />
              )}
            </ModalBody>
          </React.Fragment>
        )}
      </Container>
    </ModalFullScreen>
  )
}

LevelModalMentor.propTypes = {
  dispatch: PropTypes.func.isRequired,
  apiData: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  ids: PropTypes.object.isRequired,
  level: PropTypes.object.isRequired,
  quests: PropTypes.array.isRequired,
  handleQuestClick: PropTypes.func.isRequired,
  courseColor: PropTypes.string.isRequired,
  unsavedWork: PropTypes.array
}

export const mapStateToProps = (state, ownProps) => {
  const allCourses = [
    ...state.usercourses.currentUserCourses,
    ...state.usercourses.pastUserCourses
  ]
  const course = allCourses.find(
    c => c.master_course.id === ownProps.ids.masterCourseId
  )
  return {
    orgId: state.organization.orgId,
    token: state.auth.token,
    userId: state.auth.user.id,
    courseSectionId: course ? course.course_section_id : null,
    unsavedWork: state.unsavedwork.items
  }
}

export default connect(mapStateToProps)(withApiData(LevelModalMentor))
export { LevelModalMentor as LevelModalMentorDisconnected }
